import { useMemo } from "react"

import type { EditableTestCase } from "../model"
import {
  inferValueType,
  unifyTypes,
  type MetadataColumnType,
} from "../utils/metadataColumnType"
import type { AddedMetadataColumn } from "./useMetadataColumnState"

export interface MetadataColumn {
  key: string
  inferredType: MetadataColumnType
  /** 0..1 fraction of test cases where this key is present with a non-null value. */
  presence: number
  /** True when the column was added by the user and has no corresponding data yet. */
  isUserAdded: boolean
}

/**
 * Walk every test case's `expected_output_json`, union the top-level keys,
 * and infer a type per key from the observed values.
 *
 * Pure function; exported for testing.
 */
export function deriveMetadataColumns(
  testCases: Pick<EditableTestCase, "expected_output_json">[],
): MetadataColumn[] {
  const types = new Map<string, MetadataColumnType | null>()
  const counts = new Map<string, number>()

  for (const tc of testCases) {
    const meta = tc.expected_output_json
    if (!meta || typeof meta !== "object") {
      continue
    }
    for (const [key, value] of Object.entries(meta)) {
      const observed = inferValueType(value)
      if (observed === null) {
        // Key present but value missing — still surface the column.
        if (!types.has(key)) {
          types.set(key, null)
        }
        continue
      }
      counts.set(key, (counts.get(key) ?? 0) + 1)
      const prior = types.get(key) ?? null
      types.set(key, unifyTypes(prior, observed))
    }
  }

  const total = testCases.length || 1
  const columns: MetadataColumn[] = []
  for (const [key, type] of types.entries()) {
    columns.push({
      key,
      inferredType: type ?? "string",
      presence: (counts.get(key) ?? 0) / total,
      isUserAdded: false,
    })
  }

  columns.sort((a, b) => a.key.localeCompare(b.key))
  return columns
}

/**
 * Combine derived columns with user-added ones. A user-added column whose key
 * already appears in the data is ignored (data wins), since the inferred type
 * and presence carry more information.
 */
export function mergeAddedColumns(
  derived: MetadataColumn[],
  added: AddedMetadataColumn[],
): MetadataColumn[] {
  const keys = new Set(derived.map((c) => c.key))
  const extras: MetadataColumn[] = added
    .filter((c) => !keys.has(c.key))
    .map((c) => ({
      key: c.key,
      inferredType: c.type,
      presence: 0,
      isUserAdded: true,
    }))
  return [...derived, ...extras].sort((a, b) => a.key.localeCompare(b.key))
}

export function useMetadataColumns(
  testCases: EditableTestCase[],
  addedColumns: AddedMetadataColumn[] = [],
): MetadataColumn[] {
  return useMemo(
    () => mergeAddedColumns(deriveMetadataColumns(testCases), addedColumns),
    [testCases, addedColumns],
  )
}
